import moment from 'moment-timezone'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import axios from '../axios'
import Constants from '../constants'
import useApi from '../composables/useApi'
import { LOGIN_ROUTE_NAME, LOGOUT_API_PATH } from '../components/auth/authConfig'

const { auditActionsEventTypes, trackableDocumentTypes } = Constants

const $toast = useToast()

export const TOAST_SUCCESS_TIME = 5000
export const TOAST_ERROR_TIME = 10000

// Utility functions that do not depend on Vue instance
export function trim(source, c) {
  if (c === ']') c = '\\]'
  if (c === '\\') c = '\\\\'
  return source.replace(new RegExp('^[' + c + ']+|[' + c + ']+$', 'g'), '')
}

export function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function allIndexesOf(strSrc, char) {
  let indices = []
  for (let i = 0; i < strSrc.length; i++) {
    if (strSrc[i] === char) indices.push(i)
  }
  return indices
}

export function debounce(fn, ms) {
  let timer = 0
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(fn.bind(this, ...args), ms || 0)
  }
}

export function parseYearOverride(year) {
  let returnVal = 0
  if (year <= 2000 && year + 2000 <= moment().year()) {
    returnVal = year + 2000
  } else if (year <= 2000 && year + 1900 <= 2000) {
    returnVal = year + 2000
  } else {
    returnVal = year
  }
  if (returnVal > moment().year() + 100) {
    returnVal -= 100
  }
  return returnVal
}

export function getStatusNumber(status) {
  switch (status) {
    case 'unsent':
      return 0
    case 'sent':
      return 1
    case 'returned':
      return 2
  }
}

export function newPasswordState(siteSettings, passMatch, minLength, minAlphaLower, minAlphaUpper, minNumeric, minSymbol, minUnique, twoFields) {
  let rules = [
    {
      isValid: minLength,
      msg: `must be at least ${siteSettings.minLength} characters long`,
    },
    {
      isValid: minUnique,
      msg: `must contain at least ${siteSettings.minUnique} unique`,
    },
    {
      isValid: minAlphaUpper,
      msg: `must contain at least ${siteSettings.minAlphaUpper} upper`,
    },
    {
      isValid: minAlphaLower,
      msg: `must contain at least ${siteSettings.minAlphaLower} lower`,
    },
    {
      isValid: minNumeric,
      msg: `must contain at least ${siteSettings.minNumeric} numbers`,
    },
    {
      isValid: minSymbol,
      msg: `must contain at least ${siteSettings.minSymbol} symbols`,
    },
  ]
  if (twoFields) {
    rules.push({
      isValid: passMatch,
      msg: `passwords must match`,
    })
  }
  return rules
}

export const getFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) return null
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i
  const matches = filenameRegex.exec(contentDisposition)
  if (matches != null && matches[1]) {
    return matches[1].replace(/['"]/g, '')
  }
  return null
}

// Composable for toast and error handling
export function useGlobalUtils() {
  // Only use these if we're in a component context
  let $router = null
  let $route = null

  try {
    $router = useRouter()
    $route = useRoute()
  } catch (e) {
    // Not in a component context, these will remain null
  }

  function getTimezoneOffset() {
    let date = new Date()
    const offset = date.getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60

    return parseInt(`${offset < 0 ? '+' : '-'}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`)
  }

  function parseReportName(fullName) {
    fullName = fullName.replaceAll('—', '-').replaceAll('–', '-')
    // Updated regex to accept 3-5 characters for typeCode and 2-4 digits for year
    let exp = new RegExp(/^([0-9]{4})-([a-zA-Z0-9]{3,5})-([0-9]{2,4})-(.+)/g)
    let match = exp.exec(fullName)

    if (match) {
      let dotLoc = fullName.lastIndexOf('.')
      let extension = fullName.substr(dotLoc)

      let final = {
        institutionId: parseInt(match[1]),
        typeCode: match[2],
        yearOverride: parseInt(match[3]),
        name: match[4].substr(0, match[4].lastIndexOf('.')),
        extension: extension,
      }
      return final
    } else {
      throw new Error(`${fullName}: Name doesn't meet required format. Expected format: {institutionId}-{typeCode(3-5 chars)}-{year(2-4 digits)}-{name}.{extension}`)
    }
  }

  function formatDateInLocalTimezone(date, format) {
    return moment(date).add(getTimezoneOffset(), 'hours').format(format)
  }

  function hasPermissions(session, permissions, accessLevel = 1) {
    if (!session.permissions || session.permissions.length === 0) {
      return null
    }

    let hasPermissions = false
    session.permissions.map((permission) => {
      if (permissions.includes(permission.code) && parseInt(permission.access) >= parseInt(accessLevel)) {
        hasPermissions = true
      }
    })

    return hasPermissions ? true : null
  }

  function toast(msg, type, position, duration = 10000, nav = undefined, queue = true) {
    let message = {
      message: `${msg}`,
      type: type,
      position: position,
      duration: duration,
      dismissabçe: true,
      queue: queue,
    }

    $toast.open(message)

    if (nav && Number(nav) && $router) {
      $router.go(nav)
    } else if (nav && $router) {
      $router.push(nav).catch((err) => {
        errorToast('Navigation error', null, true)
      })
    }
  }

  function successToast(msg, nav = undefined, queue = true, duration = TOAST_SUCCESS_TIME) {
    toast(msg, 'success', 'top', duration, nav, queue)
  }

  function successToastBottom(msg, nav = undefined, queue = true, duration = TOAST_SUCCESS_TIME) {
    toast(msg, 'success', 'bottom', duration, nav, queue)
  }

  function errorToast(msg, nav = undefined, queue = true, duration = TOAST_ERROR_TIME) {
    toast(msg, 'error', 'top', duration, nav, queue)
  }

  function errorToastBottom(msg, nav = undefined, queue = true, duration = TOAST_ERROR_TIME) {
    toast(msg, 'error', 'bottom', duration, nav, queue)
  }

  function warningToast(msg, nav = undefined, queue = true, duration = TOAST_ERROR_TIME) {
    toast(msg, 'warning', 'top', duration, nav, queue)
  }

  function warningToastBottom(msg, nav = undefined, queue = true, duration = TOAST_ERROR_TIME) {
    toast(msg, 'warning', 'bottom', duration, nav, queue)
  }

  function infoToast(msg, nav = undefined, queue = true, duration = TOAST_ERROR_TIME) {
    toast(msg, 'info', 'top', duration, nav, queue)
  }

  function infoToastBottom(msg, nav = undefined, queue = true, duration = TOAST_ERROR_TIME) {
    toast(msg, 'info', 'bottom', duration, nav, queue)
  }

  async function handleApiErr(error, nav, msgPassThrough, ignoreToast = false) {
    throw error

    let msg = error

    if (error.response) {
      if (error.response.status && error.response.status === 500) {
        if (!msgPassThrough) {
          msgPassThrough = 'Unable to complete operation. Please contact system administrator.'
        }
        errorToast(msgPassThrough)
        return
      }
      if (error.response.status && error.response.status === 401 && !$route?.meta?.authScreen) {
        try {
          await axios.post(LOGOUT_API_PATH)
        } catch (error) {
          handleApiErr(error)
        } finally {
          if ($router) {
            $router.push({ name: LOGIN_ROUTE_NAME }).catch((err) => {
              handleApiErr(err, null, null, true)
            })
          }
        }

        return
      }
      if (error.response.status && error.response.status === 401) {
        return
      }
      msg = `Error: ${error.response.status}: ${error.response.statusText}`

      if (error.response.data && error.response.data.message) {
        msg = `Error: ${error.response.data.message}`

        if (error.response.data.isInputError) {
          msg = `Input Error: ${error.response.data.message}`
        }

        if (error.response.data.meta && Object.entries(error.response.data.meta).length !== 0) {
          msg += ` meta:${JSON.stringify(error.response.data.meta)}`
        }
      }
    }
    errorToast(msg, nav, true)
  }

  const deleteSelected = async (route, paramName, msgPassThrough, callback = () => {}) => {
    let paramNameLocal = 'ids'
    if (paramName) paramNameLocal = paramName

    if (!route || !route.trim || route.trim().length < 1) {
      errorToast(`DEV: this.handleSubmit ${route} parameter is missing`)
      return
    }
    this.showDeleteModal = false
    this.confirmationText = ''

    let request = {
      params: {},
    }

    request.params[paramNameLocal] = this.selectedRows

    try {
      await axios.delete(route, request)
      await refreshData()
      successToast('Deleted!')
      callback()
    } catch (err) {
      handleApiErr(err, undefined, msgPassThrough)
    }
  }

  const getUploadDate = (date) => {
    return moment(date).utc()
  }

  const loadModel = async (route, idInRoute = false) => {
    if (!route || route.trim().length < 1) {
      errorToast(`DEV: loadModel ${route} is missing`)
      return
    }
    try {
      route = trim(route, '/')
      route = `/${route}/`
      if (idInRoute && $route) {
        let id = $route.params.id
        route = `${route}${id}`
      }

      let response = await axios.get(route)

      if (response.data.chartReportingDate && response.data.chartReportingDate.length > 0) {
        response.data.chartReportingDate = response.data.chartReportingDate.split('T')[0]
      }
      return response.data
    } catch (error) {
      handleApiErr(error)
    }
  }

  async function getSiteSettings() {
    try {
      let route = `/sites/settings`
      let response = await axios.get(route)
      return response.data
    } catch (error) {
      handleApiErr(error)
    }
  }

  // Updated: always returns true or false
  const isEmail = (email) => {
    if (email === null || email === undefined) return false
    try {
      const s = String(email).toLowerCase()
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(s)
    } catch (e) {
      return false
    }
  }

  const clone = (source, deep) => {
    var _new, prop, type

    if (source !== null) {
      if (typeof source !== 'object') {
        _new = source
        return _new
      }

      _new = new source.constructor()

      for (prop in source) {
        if (source.hasOwnProperty(prop)) {
          type = typeof source[prop]

          if (deep && type === 'object') {
            _new[prop] = clone(source[prop], deep)
          } else {
            _new[prop] = source[prop]
          }
        }
      }
    }

    return _new
  }

  const downloadReport = async (route, isPrinting = false, fileName = null) => {
    try {
      const response = await axios.get(route, {
        responseType: 'blob',
      })

      if (!response || response.status !== 200) {
        errorToast('Failed to download report')
        return false
      }

      const blob = new Blob([response.data], { type: response.headers['content-type'] })
      const url = window.URL.createObjectURL(blob)

      let name = null

      if (!fileName) {
        const contentDisposition = response.headers['content-disposition']
        name = contentDisposition.split('filename=')[1].replace(/"/g, '')
      } else {
        name = fileName
      }

      if (isPrinting) {
        document.querySelectorAll('iframe[id="print-iframe"]').forEach((iframe) => iframe.remove())
        let iframe = document.createElement('iframe')
        iframe.id = 'print-iframe'
        iframe.src = url
        iframe.style.position = 'absolute'
        iframe.style.display = 'none'
        document.body.appendChild(iframe)

        iframe.onload = function () {
          setTimeout(function () {
            iframe.focus()
            iframe.contentWindow.print()
          }, 1)
        }
      } else {
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      errorToast('Error downloading, please try again later.')
      return false
    } finally {
      return true
    }
  }

  async function getPdfResponse(isDocument, needsConversion, id, path = null, fileObject = null) {
    const route = path ? path : `/${isDocument ? 'documents' : 'reports'}/${id}/content?needsConversion=${needsConversion}`

    const req = await axios.get(route, {
      responseType: needsConversion ? 'json' : 'blob',
    })

    if (req.status !== 200) {
      errorToast('Something went wrong, please try again')
      return null
    }

    if (fileObject) {
      const { typeCode } = fileObject
      // if (typeCode.trim().length > 0 && Object.values(trackableDocumentTypes).includes(typeCode)) {
      let payload = {
        actionType: auditActionsEventTypes.PREVIEW,
        actionDescription: `Opened report file of type ${typeCode}`,
        documentId: id,
        documentName: fileObject.displayName,
        documentType: typeCode,
        actionContext: JSON.stringify({ ...fileObject }),
        clientId: fileObject.institutionId,
        timestamp: moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
      }

      // Resolved lazily: useApi() at module level runs during the axios -> router -> globalUtils import cycle and captures an undefined axios.
      const api = useApi()
      let action = '-'

      switch (typeCode) {
        case trackableDocumentTypes.assetReport: {
          action = api.customAuditActions.preview_assetReportFile
          break
        }

        case trackableDocumentTypes.assetReportSummary: {
          action = api.customAuditActions.preview_summaryAssetReportFile
          break
        }

        case trackableDocumentTypes._8925Report: {
          action = api.customAuditActions.preview_8925ReportFile
          break
        }

        default: {
          action = api.customAuditActions.preview_OtherReportsFile
          break
        }
      }

      await api.logAuditAction({ action, ...payload })
      // }
    }

    return req
  }

  function prettyBytes(num) {
    if (typeof num !== 'number' || isNaN(num)) return ''
    if (num < 1024) return num + ' B'
    let units = ['KB', 'MB', 'GB', 'TB']
    let i = -1
    do {
      num = num / 1024
      i++
    } while (num >= 1024 && i < units.length - 1)
    return num.toFixed(1) + ' ' + units[i]
  }

  function base64ToArrayBuffer(base64) {
    var binary_string = window.atob(base64)
    var len = binary_string.length
    var bytes = new Uint8Array(len)
    for (var i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i)
    }
    return bytes.buffer
  }

  const idToPeriod = (isDate = false) => {
    try {
      if (!$route) {
        return ''
      }

      const charset = ($route.params.id + '').split('')

      if (!isDate) {
        let val = `${charset.slice(0, 4).join('')}-${charset.slice(4, charset.length).join('')}`

        // if - is last char, remove it
        if (val.slice(-1) === '-') {
          val = val.slice(0, -1)
        }

        return val
      }

      return moment(`${charset.slice(0, 4).join('')}-${charset.slice(4, charset.length).join('')}-1`)
        .add(1, 'month')
        .subtract(1, 'day')
        .format('YYYY-MM-DD')
    } catch (e) {
      return ''
    }
  }

  const getFileSize = (fileSize) => {
    let label = 'b'
    // let number = 0
    // let round = 0

    while (fileSize / 1024 > 1) {
      fileSize = fileSize / 1024
      label = label === 'b' ? 'kb' : label === 'kb' ? 'mb' : label === 'mb' ? 'gb' : label === 'gb' ? 'tb' : label === 'tb' ? 'pb' : label === 'pb' ? 'eb' : label === 'eb' ? 'zb' : label === 'zb' ? 'yb' : label
    }

    return Intl.NumberFormat().format(fileSize.toFixed(0)) + ` ${label.toUpperCase()}`
  }

  const getTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  const validatePassword = (newPassword, confirmPassword, siteSettings, twoFields = true) => {
    if (!siteSettings || !siteSettings.minLength) {
      siteSettings = {
        minLength: 0,
        minAlphaUpper: 0,
        minAlphaLower: 0,
        minNumeric: 0,
        minSymbol: 0,
        minUnique: 0,
      }
    }
    let matchPassword = newPassword === confirmPassword
    if (!newPassword || newPassword.length === 0) {
      return newPasswordState(siteSettings, matchPassword, false, false, false, false, false)
    }
    let minLength = newPassword.length >= siteSettings.minLength

    let numUpper = (newPassword.match(/[A-Z]/g) || []).length
    let minAlphaUpper = numUpper >= siteSettings.minAlphaUpper

    let numLower = (newPassword.match(/[a-z]/g) || []).length
    let minAlphaLower = numLower >= siteSettings.minAlphaLower

    let numNumeric = (newPassword.match(/[0-9]/g) || []).length
    let minNumeric = numNumeric >= siteSettings.minNumeric
    let numSymbol = (newPassword.match(/[-@#\\!$%^&*()_+|~=`{}[\]:";'<>?,./]/g) || []).length
    let minSymbol = numSymbol >= siteSettings.minSymbol

    let charMap = new Map()
    for (var i = 0; i < newPassword.length; i++) {
      charMap.set(newPassword[i], true)
    }
    let numUniq = charMap.size
    let minUniq = numUniq >= siteSettings.minUnique

    return newPasswordState(siteSettings, matchPassword, minLength, minAlphaLower, minAlphaUpper, minNumeric, minSymbol, minUniq, twoFields)
  }

  const formatMoney = (amount, includeCents = true) => {
    var formatter = new Intl.NumberFormat()

    if (includeCents) {
      // Create our number formatter.
      formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    } else {
      formatter = Intl.NumberFormat('en-US')
    }

    return formatter.format(amount)
  }

  const getTotals = (object, field, divisor = 1) => {
    let total = 0

    object.map((record) => {
      total += record[field]
    })

    return total / divisor
  }

  function removeHtmlTags(str) {
    if (!str) return ''
    let cleaned = str.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    cleaned = cleaned.replace(/<[^>]*>/g, '')
    cleaned = cleaned.replace(/&nbsp;|&#160;/gi, ' ')
    const txt = document.createElement('textarea')
    txt.innerHTML = cleaned
    cleaned = txt.value
    return cleaned.replace(/\s+/g, ' ').trim()
  }

  return {
    removeHtmlTags,

    getTotals,
    formatMoney,
    idToPeriod,
    validatePassword,
    isEmail,
    getFileSize,
    getTimeZone,
    getPdfResponse,
    successToast,
    successToastBottom,
    errorToast,
    errorToastBottom,
    warningToast,
    warningToastBottom,
    infoToast,
    infoToastBottom,
    handleApiErr,
    hasPermissions,
    base64ToArrayBuffer,
    formatDateInLocalTimezone,
    getTimezoneOffset,
    deleteSelected,
    parseReportName,
    loadModel,
    getUploadDate,
    clone,
    downloadReport,
    prettyBytes,
    getSiteSettings,
  }
}
